import { Injectable } from '@angular/core';
import { CapacitorHttp, HttpOptions, HttpResponse } from '@capacitor/core';
import { TokenService } from '../auth/token.service';
import { LoginService } from '../auth/login.service';
import { AlertService } from '../base/alert.service';
import { OfflineService } from '../manager/offline.service';
import { SubscriberService } from '../manager/subscriber.service';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private token: TokenService,
    private login: LoginService,
    private alert: AlertService,
    private offline: OfflineService,
    private sub: SubscriberService,
  ) {}

  private unauthorizedNotified = false;

  private async performRequest(options: HttpOptions): Promise<HttpResponse> {
    try {
      const response = await CapacitorHttp.request(options);
      if (response.status >= 400) {
        Sentry.captureException(new Error(`HTTP ${response.status} ${options.method} ${options.url}`), {
          extra: { status: response.status, url: options.url, method: options.method, responseData: response.data },
        });
      }
      return response;
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status;
      if (typeof status === 'number') {
        await this.handleUnauthorized(status);
      }
      Sentry.captureException(error, {
        extra: { url: options.url, method: options.method },
      });
      throw error;
    }
  }

  private async handleUnauthorized(status: number) {
    if (status !== 401) {
      return;
    }

    // If there's no token, it's likely a login attempt.
    if (!this.login.loginToken) {
      return;
    }

    if (this.unauthorizedNotified) {
      return;
    }

    this.unauthorizedNotified = true;

    // Aktuellen Auftrag sofort sichern bevor die Session endet
    const currentOrder = this.sub.currentOrder.getValue();
    if (currentOrder) {
      await this.offline.setCurrentOrder(currentOrder, true, true);
    }

    const pendingOrders = this.sub.offlineOrders.getValue() ?? [];
    const hasPending = pendingOrders.length > 0;

    const creds = await this.alert.presentReloginAlert(this.login.decodedToken?.email ?? '');

    if (!creds) {
      // User hat Dialog weggedrückt — nur ausloggen wenn keine Pending-Orders
      this.unauthorizedNotified = false;
      if (!hasPending) {
        await this.login.resetToken(true);
      }
      return;
    }

    try {
      const serverUrl = this.token.serverUrls.kontorserver;
      const res = await CapacitorHttp.request({
        url: `${serverUrl}/api/users/login`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { email: creds.email, password: creds.password },
      });
      if (res.status === 200 && res.data?.token) {
        await this.login.logmein(res);
        this.unauthorizedNotified = false;
        if (hasPending) {
          await this.alert.presentToast(`Eingeloggt – synchronisiere ${pendingOrders.length} Auftrag/Aufträge...`, 4000);
          this.sub.requestSync();
        }
        return;
      }
    } catch { /* fällt durch */ }

    await this.alert.presentToast('Login fehlgeschlagen – Daten bleiben lokal gesichert.', 5000, 'warning');
    await this.login.resetToken(true);
    setTimeout(() => { this.unauthorizedNotified = false; }, 5000);
  }

  async refreshLoginToken(): Promise<boolean> {
    if (!this.login.loginToken) {
      return false;
    }

    try {
      const serverUrl = this.token.serverUrls.kontorserver;
      const res = await CapacitorHttp.request({
        url: `${serverUrl}/api/users/refresh-token`,
        method: 'POST',
        headers: this.getHeaders(),
      });

      if ((res.status === 200 || res.status === 201) && res.data?.refreshedToken) {
        await this.login.updateToken(res.data.refreshedToken);
        return true;
      }

      return false;
    } catch (error) {
      Sentry.captureException(error, { extra: { context: 'refreshLoginToken' } });
      return false;
    }
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    headers['Authorization'] = `Bearer ${this.login.loginToken}`;
    headers['kontor'] = `${this.login.decodedToken?.role}`;

    return headers;
  }

  // HTTP Methods
  async getRequest<T>(
    baseUrl: string,
    endpoint: string,
    params?: Record<string, any>
  ): Promise<{ data: T, status: number }> {
    const options: HttpOptions = {
      url: `${baseUrl}/${endpoint}`,
      method: 'GET',
      params,
      headers: this.getHeaders(),
    };

    const response: HttpResponse = await this.performRequest(options);
    await this.handleUnauthorized(response.status);
    return { data: response.data as T, status: response.status };
  }

  async postRequest<T>(
    baseUrl: string,
    endpoint: string,
    body: any
  ): Promise<{ data: T, status: number }> {
    const options: HttpOptions = {
      url: `${baseUrl}/${endpoint}`,
      method: 'POST',
      headers: this.getHeaders(),
      data: body,
    };

    const response: HttpResponse = await this.performRequest(options);
    await this.handleUnauthorized(response.status);
    return { data: response.data as T, status: response.status };
  }

  async putRequest<T>(
    baseUrl: string,
    endpoint: string,
    body: any
  ): Promise<{ data: T, status: number }> {
    const options: HttpOptions = {
      url: `${baseUrl}/${endpoint}`,
      method: 'PUT',
      headers: this.getHeaders(),
      data: body,
    };

    const response: HttpResponse = await this.performRequest(options);
    await this.handleUnauthorized(response.status);
    return { data: response.data as T, status: response.status };
  }

  async patchRequest<T>(
    baseUrl: string,
    endpoint: string,
    body: any
  ): Promise<{ data: T, status: number }> {
    const options: HttpOptions = {
      url: `${baseUrl}/${endpoint}`,
      method: 'PATCH',
      headers: this.getHeaders(),
      data: body,
    };

    const response: HttpResponse = await this.performRequest(options);
    await this.handleUnauthorized(response.status);
    return { data: response.data as T, status: response.status };
  }

  async deleteRequest<T>(baseUrl: string, endpoint: string): Promise<{ data: T, status: number }> {
    const options: HttpOptions = {
      url: `${baseUrl}/${endpoint}`,
      method: 'DELETE',
      headers: this.getHeaders(),
    };

    const response: HttpResponse = await this.performRequest(options);
    await this.handleUnauthorized(response.status);
    return { data: response.data as T, status: response.status };
  }
}
